import React, { useRef, useEffect, useState } from 'react'
import map from 'lodash/map'
import filter from 'lodash/filter'
import { useSelector, useDispatch } from 'react-redux'
import WorkspaceRow from './WorkspaceRow'
import Button from '@SiteComponents/Button'
import WorkspacesBulkActionBar from '@SiteContainers/WorkspacesBulkActionBar'
import Plus from '@Images/projects_table_new_project_plus.svg'

import {
  getAllWorkspaceUsers,
} from '@SiteModules/WorkspaceUser/selectors'

import {
  getWorkspaces,
  getSelectedWorkspaceIds,
  getAllWorkspacesSelected,
} from '@SiteModules/Workspace/selectors'

import {
  changeSelectedWorkspaces,
  selectAllWorkspaces,
  unselectWorkspaces,
} from '@SiteModules/Workspace/actions'

import {
  openCreateWorkspacePopup
} from '@SiteModules/Popup/actions'

import './WorkspacesTable.scss'

interface WorkspacesTableProps {
  setOpenedWorkspaceId(openedWorkspaceId: number): void
}

const WorkspacesTable: React.FC<WorkspacesTableProps> = props => {
  const dispatch = useDispatch()

  const workspaces = useSelector(getWorkspaces)
  const selectedWorkspaceIds = useSelector(getSelectedWorkspaceIds)
  const allWorkspacesSelected = useSelector(getAllWorkspacesSelected)
  const allWorkspaceUsers = useSelector(getAllWorkspaceUsers)

  const [hovering, setHovering] = useState(false)
  const checkboxRef = useRef<HTMLLabelElement | null>(null)
  const hasWorkspacesSelected = selectedWorkspaceIds.length > 0

  useEffect(() => {
    if (allWorkspacesSelected) {
      checkboxRef.current?.style?.setProperty('visibility', 'visible')
    } else if (!hovering) {
      checkboxRef.current?.style?.setProperty('visibility', 'hidden')
    }
  }, [allWorkspacesSelected])

  const onMouseEnter = () => {
    setHovering(true)
    checkboxRef.current?.style?.setProperty('visibility', 'visible')
  }

  const onMouseLeave = () => {
    setHovering(false)
    if (allWorkspacesSelected) { return }

    checkboxRef.current?.style?.setProperty('visibility', 'hidden')
  }

  const handleChangeSelectedWorkspaces = (e: React.ChangeEvent<HTMLInputElement>, workspaceId: number) => {
    dispatch(changeSelectedWorkspaces({ workspaceId, selectWorkspace: e.target.checked }))
  }

  const handleSelectAllWorkspaces = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      dispatch(selectAllWorkspaces())
    } else {
      dispatch(unselectWorkspaces())
    }
  }

  const handleOpenCreateWorkspacePopup = () => {
    dispatch(openCreateWorkspacePopup())
  }

  const renderNewWorkspaceButton = () => {
    return (
      <div className="WorkspacesTable__button">
        <Button
          caption="New workspace"
          icon={<Plus />}
          styleType="brand"
          size="large"
          onClick={handleOpenCreateWorkspacePopup}
        />
      </div>
    )
  }

  const renderTable = () => {
    return (
      <table className="WorkspacesTable__table">
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody className="WorkspacesTable__body">
          {tableRows}
        </tbody>
      </table>
    )
  }

  const renderTableHeader = () => {
    return (
      <tr
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <th className="WorkspacesTable__check">
          {renderCheckbox()}
        </th>
        <th className="WorkspacesTable__workspace">
          Workspace
        </th>
        <th className="WorkspacesTable__members">
          Members
        </th>
        <th className="WorkspacesTable__projects">
          Projects
        </th>
        <th className="WorkspacesTable__creation_date">
          Creation date
        </th>
      </tr>
    )
  }

  const tableRows = map(workspaces, (workspace, index) => {
    const selected = selectedWorkspaceIds.includes(workspace.id)
    const relevantWorkspaceUsers = filter(allWorkspaceUsers, workspaceUser => workspaceUser.attributes.workspaceId === workspace.id)

    return (
      <WorkspaceRow
        key={index}
        index={index}
        workspace={workspace}
        numberOfWorkspaceUsers={relevantWorkspaceUsers.length}
        changeSelectedWorkspaces={handleChangeSelectedWorkspaces}
        selected={selected}
        setOpenedWorkspaceId={props.setOpenedWorkspaceId}
      />
    )
  })

  const renderCheckbox = () => {
    return (
      <label className="WorkspacesTable__checkbox" ref={checkboxRef}>
        <input
          type="checkbox"
          onChange={handleSelectAllWorkspaces}
          checked={allWorkspacesSelected}
        />
        <div className="WorkspacesTable__checkmark" />
      </label>
    )
  }

  return (
    <div className="WorkspacesTable">
      {renderNewWorkspaceButton()}
      {renderTable()}
      {hasWorkspacesSelected && (
        <WorkspacesBulkActionBar />
      )}
    </div>
  )
}

export default WorkspacesTable
